<template>
  <my-progress-graph @dataset-loaded="datasetLoaded" :filters="filters" :header-size="245">
    <template v-slot:header="{ rangedDataset, oldestCfMonth }">
      <my-progress-savings-header :ranged-dataset="rangedDataset" :oldest-cf-month="oldestCfMonth"
                                  :filters="filters" @filters-changed="filtersChanged"/>
    </template>
    <template v-slot:graph="graphSlotProps">
      <div class="graph-labels">
        <share-button filename="my-savings-progress" share-name="MySavingsProgress"
                      :share-component="shareComponent"
                      :share-component-props="getShareComponentProps(graphSlotProps)"
                      :segment-extra-props="{ filters }" v-slot="{ onClick }">
          <icon class="share-icon" icon-name="share" fill weight="bold" tabindex="0" @click="onClick"
                @keyup.enter="onClick"/>
        </share-button>
      </div>
      <bar-graph-v2 v-bind="getBarGraphProps(graphSlotProps)" />
    </template>
  </my-progress-graph>
</template>

<script>
import Segment from '@/Segment';
import _ from 'lodash';
import BaseUI from '@riseupil/base-ui';
import BarGraphV2 from '@/base-components/bar-graph-v2/BarGraphV2';
import { mapActions } from 'vuex';
import dateUtils from '@/utils/dates';
import ShareButton from '@/base-components/share/ShareButton';
import { DATE_FILTERS_STRINGS, SAVINGS_DATA_SECTION_FILTERS_STRINGS } from '../my-progress-filters.consts';
import { cashflowSavings, excludedSavings, getSavingsSum } from './my-progress-saving.utils';
import MyProgressSavingsHeader from './MyProgressSavingsHeader.vue';
import MyProgressGraph from '../MyProgressGraph';
import MyProgressShareAsset from '../MyProgressShareAsset';

export default {
  name: 'MyProgressSavingsSummary',
  components: {
    MyProgressGraph,
    BarGraphV2,
    MyProgressSavingsHeader,
    ShareButton,
    Icon: BaseUI.Icon,
  },
  data() {
    return {
      filters: {
        dataSections: {
          ...SAVINGS_DATA_SECTION_FILTERS_STRINGS.cashflowsAndExcluded,
          excluded: true,
          cashflows: true,
        },
        dates: {
          filterKey: 'last-12-months',
          ...DATE_FILTERS_STRINGS['last-12-months'],
          start: null,
          end: null,
        },
      },
      barStyles: {
        backgroundColor: BaseUI.Colors.riseupOrange,
        width: '12px',
        borderRadius: '10px',
      },
      shareComponent: MyProgressShareAsset,
    };
  },
  created() {
    Segment.trackUserGot('MyProgress_Savings_Graph_Shown');
    this.setAppHeaderProps({
      backgroundColorVariant: BaseUI.LayoutConsts.COLOR_VARIANT.BEIGE,
      textColor: BaseUI.Colors.riseupDarkGreen,
      logoColor: BaseUI.Colors.riseupDarkGreen,
    });
  },
  methods: {
    ...mapActions('appHeader', ['setAppHeaderProps']),
    datasetLoaded({
      end,
      start,
    }) {
      this.filters.dates.start = start;
      this.filters.dates.end = end;
    },
    valueFormatter({ cashflowMonth, ...otherValues }) {
      if (_.isEmpty(otherValues)) {
        return null;
      }
      const {
        excludedSavings,
        oneTimeSavingActualSum,
        savingEnvelopesActualsSum,
      } = otherValues;
      const value = 0
          + (this.filters.dataSections.cashflows ? oneTimeSavingActualSum + savingEnvelopesActualsSum : 0)
          + (this.filters.dataSections.excluded ? Math.abs(excludedSavings) : 0);
      return { value };
    },
    filtersChanged(filters) {
      this.filters = filters;
    },
    getBarGraphProps({ rangedDataset, labelFormatter, xTickFormatter, yTickFormatter }) {
      return {
        rawValues: rangedDataset,
        valueFormatter: this.valueFormatter,
        labelFormatter,
        xTickFormatter,
        yTickFormatter,
        barStyles: this.barStyles,
        barEdgeMarks: false,
      };
    },
    getShareComponentProps(graphSlotProps) {
      return {
        barGraphProps: this.getBarGraphProps(graphSlotProps),
        title: 'החסכונות שלי',
        formattedDatesRange:
            `${dateUtils.getMonthAndShortYear(this.filters.dates.start)} - ${dateUtils.getMonthAndShortYear(this.filters.dates.end)}`,
        sum: getSavingsSum(cashflowSavings(graphSlotProps.rangedDataset), excludedSavings(graphSlotProps.rangedDataset),
          this.filters.dataSections),
        formattedDataSections: this.filters.dataSections.excluded ? 'סה"כ חסכת' : 'סה"כ חסכת בתזרים בלבד',
        activationDateVisible: graphSlotProps.activationDateVisible,
        withBadge: true,
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@riseupil/base-ui/src/scss/spacings';

.graph-labels {
  display: flex;
  flex-direction: column;
  margin: $ri-spacing-sm $ri-spacing-xs 0;
  align-items: flex-end;
}

.share-icon {
  cursor: pointer;
}
</style>
